const { getDependencyClass } = require('../../lib/dependencies.js');
const { waitForElement } = require('../../lib/util.js');

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * PhoenixQuoteMediaQmodLoader is a generic webcomponent wrapper to put
     * a QMOD on a page; it handles setup for v1 and v2 QMOD widgets. There
     * is no Tempest wrapper provided for these widgets, so QMODs that require
     * more integration than simply the QMOD on the page need dedicated
     * components. As of July 2022, we are using only a single v2 QMOD, so the
     * default behavior is *not* to load the v2 script from Quote Media's site,
     * as a performance tweak; any widget identified as using v2 will trigger
     * this script's load via `addScriptResourceOnce()`.
     *
     * @example
     *  <phoenix-quote-media-qmod-loader
     *      class="m-quote-media-qmod-loader"
     *      config='{"qmodConfig":{"collapseDescription":true,
     *          "descCharLimit":500,"description":"full"},
     *          "qmodType":"companyprofile",
     *          "symbol":"MSFT"}'>
     *  </phoenix-quote-media-qmod-loader>
     */
    class PhoenixQuoteMediaQmodLoader extends pageClass.PhoenixElement {
        get isInTabPanel() {
            return !!this.config?.isInTabPanel;
        }

        get tabsContainer() {
            if (!this._tabsContainer) {
                this._tabsContainer = this.closest('phoenix-tabs');
            }

            return this._tabsContainer;
        }

        get tabPanel() {
            if (!this._tabPanel) {
                this._tabPanel = this.closest('phoenix-tab-panel');
            }

            return this._tabPanel;
        }

        get observedBroadcastedEvents() {
            if (!this.isInTabPanel) {
                return null;
            }

            return ['phoenix:tab:active'];
        }

        broadcastedEventCallback(name, args = {}) {
            switch (name) {
                case 'phoenix:tab:active':
                    const activePanelId = args.detail && args.detail.panelId;
                    if (!this.tabPanel || !this._isInActivePanel(activePanelId)) {
                        break;
                    }
                    this._loadV1QMod();
                    this._loadV2QMod();
                    break;
            }
        }

        connectedCallback() {
            // Set up broadcasted event listeners, etc.
            super.connectedCallback();
            // Per-item config
            const tool = this.config?.qmodType;
            const symbol = this.config?.symbol;
            const qmodConfig = this.config?.qmodConfig || {};
            if (!tool) {
                // eslint-disable-next-line no-console
                console.error('Misconfigured QMOD loader component');
                return;
            }

            const wmid = this.config?.wmid;
            const v2Src = this.config?.v2Src;
            if (this.config.version === 'v2') {
                if (!wmid || !v2Src) {
                    // eslint-disable-next-line no-console
                    console.error('Missing required qmodii attribute in QMOD script tag');
                    return;
                }
                qmodConfig.wmid = wmid;
                // Note: this is an object property to work around an environmental
                // issue in our build machine running the tests, not for any
                // substantive reason.
                this.qmodiiResourcePromise = pageClass.util.addScriptResourceOnce(v2Src, {
                    id: 'qmodiiLoader',
                });
            }
            // Universal values.
            let commonConfig = {
                lang: 'en',
                lowHigh: false,
                showLogo: false,
                symbol,
            };
            // If needed in the future we can distinguish between v2 and some
            // theoretical v3. qmodiiResourcePromise's truthiness is being used
            // solely to distinguish between v1 and v2.
            if (this.qmodiiResourcePromise) {
                this._render_v2();
            } else {
                this._render_v1({ ...commonConfig, ...qmodConfig });
            }
        }

        _render_v1(config) {
            const classes = this._shouldPostponeLoading() ? 'qtool-hold' : 'qtool';
            let qmodConfig = JSON.stringify(config);
            this.innerHTML = `<div data-qmod-tool="${this.config.qmodType}"
            data-qmod-params=${qmodConfig}
            class=${classes}></div>`;
        }

        _render_v2() {
            if (this._shouldPostponeLoading()) {
                return;
            }
            this._loadV2QMod();
        }

        _loadV1QMod() {
            if (this.config?.version === 'v2') {
                return;
            }

            // Tool has already been loaded.
            if (this.querySelector('.qtool')) {
                return;
            }

            // Loading was previously postponed, so we fetch the container to load.
            const qModTool = this.querySelector('.qtool-hold');
            if (!qModTool) {
                // eslint-disable-next-line no-console
                console.error(`quoteMediaQModLoader: _loadQMod could not find qModTool`);
                return;
            }

            // Function used to load the QMod.
            const loadQMod = () => {
                qModTool.id = qModTool.id || Math.random().toString(36).substr(2, 9);
                qModTool.classList.replace('qtool-hold', 'qtool');
                window.qMod.loadTool(qModTool.id);
            };

            waitForElement(document, '#qmod').then((scriptElement) => {
                if (window.qMod) {
                    loadQMod();
                    return;
                }
                scriptElement.addEventListener('load', loadQMod);
            });
        }

        _loadV2QMod() {
            if (this.config.version !== 'v2') {
                return;
            }

            let qmodiiPromise = new Promise((resolve) => {
                if (typeof window.qModii !== 'undefined') {
                    resolve();
                } else {
                    let scriptElement = document.getElementById('qmodiiLoader');
                    if (scriptElement) {
                        scriptElement.addEventListener('load', resolve);
                    }
                }
            });
            qmodiiPromise.then(() => {
                // Tool has already been loaded.
                if (this.querySelector('.qmodii')) {
                    return;
                }
                const targetId = `qmod-${this.config.qmodType}-` + Math.random().toString(36).substr(2, 9);
                const container = document.createElement('div');
                container.setAttribute('class', 'qmodii');
                const target = document.createElement('div');
                target.setAttribute('id', targetId);
                container.appendChild(target);
                this.appendChild(container);
                const params = { PropSymbols: this.config.symbol, qmLanguage: 'en' };
                qModii.init(this.config.wmid, `#${targetId}`, this.config.qmodType, params);
            });
        }

        _shouldPostponeLoading() {
            if (!this.isInTabPanel) {
                return false;
            }

            const activeTabId = this.tabsContainer?.activeTabId;
            // activeTabId is not set yet, we will receive 'phoenix:tab:active' event.
            if (!activeTabId) {
                return true;
            }

            // activeTabId has been set check if it's a match.
            return !this._isInActivePanel(activeTabId);
        }

        _isInActivePanel(activePanelId) {
            const tabsId = this.tabsContainer?.id;
            const tabPanelId = this.tabPanel?.id;
            return activePanelId === tabPanelId || activePanelId === `${tabsId}-${tabPanelId}`;
        }
    }

    window.customElements.define('phoenix-quote-media-qmod-loader', PhoenixQuoteMediaQmodLoader);
});
